import moment from "moment";

/**
 * Le offerte su lezioni private ("ListiniOfferta"): fascia oraria fissa, numero di persone fisso,
 * prezzo a forfait, valide in un intervallo di date. Stessa logica già usata da PortalHybrid
 * (features/catalog/utils/privateOffers.ts), qui in JS per riuso nella "new" private lesson flow.
 *
 * La sync fa un fan-out di una promo su più righe (una per periodo della sua stagionalità), quindi
 * una singola offerta commerciale arriva come più righe con range adiacenti e stesso orario/persone
 * /prezzo. Il raggruppamento sotto le collassa in una, tenendo l'unione dei range di validità.
 */

/** "HH:mm:ss" e "HH:mm" arrivano entrambi a seconda del serializzatore; confronto e mostro su "HH:mm". */
export function toHourMinute(time) {
  return typeof time === "string" && time.length >= 5 ? time.slice(0, 5) : time;
}

/** "yyyy-MM-dd", la forma usata sia dall'API che dalle chiavi giorno del calendario. */
function toDateKey(value) {
  return typeof value === "string" && value.length >= 10 ? value.slice(0, 10) : value;
}

/**
 * Colleziona le righe che descrivono la stessa offerta commerciale (stessa fascia, stesse
 * persone, stesso prezzo) in un unico gruppo con l'unione dei suoi periodi di validità.
 */
export function groupPrivateOffers(offers) {
  if (!Array.isArray(offers) || offers.length === 0) return [];

  const byKey = new Map();

  for (const offer of offers) {
    const startTime = toHourMinute(offer.startTime);
    const endTime = toHourMinute(offer.endTime);
    const allDay = offer.allDay === true;
    // Pubblica e offerta d'agenzia sullo stesso slot hanno prezzi diversi apposta: se le
    // raggruppassimo insieme uno dei due prezzi sparirebbe arbitrariamente.
    const agencyId = offer.agencyId ?? null;
    const key = `${startTime}_${endTime}_${offer.numberOfPeople}_${offer.totalPrice}_${allDay}_${agencyId ?? "public"}`;

    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        startTime,
        endTime,
        numberOfPeople: offer.numberOfPeople,
        totalPrice: offer.totalPrice,
        currency: offer.currency,
        allDay,
        agencyId,
        validityRanges: [],
      };
      byKey.set(key, group);
    }

    group.validityRanges.push({
      validFrom: toDateKey(offer.validFrom),
      validTo: toDateKey(offer.validTo),
    });
  }

  for (const group of byKey.values()) {
    group.validityRanges.sort((a, b) => (a.validFrom < b.validFrom ? -1 : a.validFrom > b.validFrom ? 1 : 0));
  }

  return Array.from(byKey.values()).sort(
    (a, b) =>
      a.startTime.localeCompare(b.startTime) ||
      a.endTime.localeCompare(b.endTime) ||
      a.numberOfPeople - b.numberOfPeople ||
      a.totalPrice - b.totalPrice ||
      Number(a.allDay) - Number(b.allDay) ||
      String(a.agencyId).localeCompare(String(b.agencyId))
  );
}

/** True quando il gruppo è in vendita su dateKey ("yyyy-MM-dd"). */
export function coversDate(group, dateKey) {
  return group.validityRanges.some(
    (range) => dateKey >= range.validFrom && dateKey <= range.validTo
  );
}

/**
 * Quanti giorni di tolleranza attorno a un giorno selezionato: un pacchetto che gira la settimana
 * dopo è comunque un suggerimento sensato, uno di tre mesi dopo no. Stesso valore del nuovo portale.
 */
export const NEARBY_DAYS = 7;

/**
 * True se il corso (pacchetto CorsoPrivato) gira vicino ad almeno uno dei giorni scelti.
 *
 * Usa le singole finestre di disponibilità e non l'estensione min-max: un corso può girare a
 * blocchi separati da mesi, e min-max lo farebbe comparire anche nei periodi morti in mezzo.
 * Porting di `courseRunsNearSelectedDays` del nuovo portale.
 */
export function courseRunsNearSelectedDays(
  corso,
  selectedDates,
  nearbyDays = NEARBY_DAYS
) {
  if (!Array.isArray(selectedDates) || selectedDates.length === 0) return false;

  const finestre = corso?.disponibilita;

  // Campo assente = backend più vecchio che non espone ancora le finestre. Non sapendo quando il
  // corso gira non possiamo giudicarlo lontano: meglio mostrarlo (comportamento precedente al
  // filtro) che farlo sparire in silenzio a ogni disallineamento di versione fra API e frontend.
  // Array vuoto è invece un'informazione vera — nessuna finestra — e lì si nasconde.
  if (finestre === undefined || finestre === null) return true;
  if (!Array.isArray(finestre) || finestre.length === 0) return false;

  return selectedDates.some((selectedDate) => {
    const day = moment(selectedDate);
    if (!day.isValid()) return false;

    const windowStart = day.clone().subtract(nearbyDays, "day").startOf("day");
    const windowEnd = day.clone().add(nearbyDays, "day").endOf("day");

    return finestre.some((finestra) => {
      const dal = moment(finestra.dal).startOf("day");
      const al = moment(finestra.al).endOf("day");
      if (!dal.isValid() || !al.isValid()) return false;

      return !dal.isAfter(windowEnd) && !al.isBefore(windowStart);
    });
  });
}

/**
 * Le offerte che ha senso mostrare per la selezione corrente.
 *
 * Un gruppo sopravvive quando il numero di persone coincide e **ogni** giorno selezionato è
 * coperto da **qualche** range del gruppo: un giorno fuori dall'offerta non avrebbe il prezzo
 * promozionale, quindi pubblicizzarla sarebbe una bugia. Nasconde solo le offerte che davvero
 * non tornano, non l'intera vetrina.
 */
export function selectVisibleOffers(groups, selectedDates, numberOfPeople) {
  if (numberOfPeople === null || numberOfPeople === undefined) return [];
  if (!Array.isArray(selectedDates) || selectedDates.length === 0) return [];

  const dateKeys = selectedDates.map((date) =>
    moment(date).format("YYYY-MM-DD")
  );

  const eleggibili = groups.filter(
    (group) =>
      group.numberOfPeople === numberOfPeople &&
      dateKeys.every((dateKey) => coversDate(group, dateKey))
  );

  return preferAgencyOffers(eleggibili);
}

/** Stessa fascia e stesse persone: l'asse su cui pubblica e agenzia si contendono lo slot. */
function slotKey(group) {
  return `${group.startTime}_${group.endTime}_${group.numberOfPeople}`;
}

/**
 * Con un'agenzia attiva il backend manda sia le sue offerte sia quelle pubbliche. Dove le due
 * descrivono lo stesso slot per lo stesso numero di persone vale quella dell'agenzia, quindi
 * mostrarle entrambe proporrebbe al cliente una scelta che non ha.
 *
 * Volutamente applicato DOPO il filtro sulle date: l'offerta d'agenzia può coprire solo una parte
 * della stagione, e togliere comunque quella pubblica lascerebbe scoperti i giorni fuori periodo.
 */
export function preferAgencyOffers(groups) {
  const slotConOffertaAgenzia = new Set(
    groups.filter((group) => group.agencyId != null).map(slotKey)
  );

  if (slotConOffertaAgenzia.size === 0) return groups;

  return groups.filter(
    (group) => group.agencyId != null || !slotConOffertaAgenzia.has(slotKey(group))
  );
}
